import { useState, useEffect } from "react";
import { getAllStats, getMonthName } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { 
  CheckCircle2, 
  Circle, 
  AlertCircle,
  Calendar,
  ClipboardList,
  ArrowRight,
  Users,
  Bug,
  HandMetal,
  ShieldCheck,
  Syringe,
  Phone,
  FileEdit
} from "lucide-react";

const YEARS = [2024, 2025, 2026, 2027];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// Define required data sections matching the Excel scorecard
const DATA_SECTIONS = [
  {
    id: "census",
    name: "Census Data",
    icon: Users,
    fields: ["patient_days", "admissions"],
    required: true,
    description: "Patient days and admissions for the month"
  },
  {
    id: "device_days",
    name: "Device Days",
    icon: Syringe,
    fields: ["urinary_catheter_days", "central_line_days"],
    required: true,
    description: "Urinary catheter days and central line days"
  },
  {
    id: "hai",
    name: "Hospital Acquired Infections",
    icon: Bug,
    fields: ["cauti_count", "suti_count", "abuti_count", "clabsi_count", "primary_bsi_count", "skin_soft_tissue_count", "pneumonia_count", "cdiff_count", "mdro_count"],
    required: true,
    description: "All HAI counts (CAUTI, CLABSI, C.diff, MDRO, etc.)"
  },
  {
    id: "labid",
    name: "Hospital-Onset LabID Events",
    icon: Bug,
    fields: ["ho_mdro_labid_count", "ho_cdi_labid_count"],
    required: false,
    description: "HO-MDRO and HO-CDI LabID events"
  },
  {
    id: "hand_hygiene",
    name: "Hand Hygiene Compliance",
    icon: HandMetal,
    fields: ["hand_hygiene_compliant", "hand_hygiene_total"],
    required: true,
    description: "Hand hygiene observations (compliant/total)"
  },
  {
    id: "isolation_ppe",
    name: "Isolation PPE Compliance",
    icon: ShieldCheck,
    fields: ["isolation_ppe_compliant", "isolation_ppe_total"],
    required: true,
    description: "Isolation PPE observations (compliant/total)"
  },
  {
    id: "ic_eoc",
    name: "IC Environment of Care",
    icon: ClipboardList,
    fields: ["ic_eoc_compliant", "ic_eoc_total"],
    required: false,
    description: "IC EOC rounds (typically quarterly)"
  },
  {
    id: "dialysis_eoc",
    name: "Dialysis EOC",
    icon: ClipboardList,
    fields: ["dialysis_eoc_compliant", "dialysis_eoc_total"],
    required: false,
    description: "Dialysis EOC rounds"
  },
  {
    id: "exposures",
    name: "Bloodborne Pathogen Exposures",
    icon: AlertCircle,
    fields: ["sticks", "sharps", "splashes"],
    required: false,
    description: "Needlesticks, sharps injuries, splashes"
  },
  {
    id: "callins",
    name: "Employee Call-ins",
    icon: Phone,
    fields: ["respiratory_callins", "gi_callins", "skin_callins", "other_callins"],
    required: false,
    description: "Communicable illness call-ins"
  }
];

export default function DataStatus() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState({});
  const navigate = useNavigate();
  const currentMonth = new Date().getMonth() + 1;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const stats = await getAllStats(year);
        // Convert to lookup by month
        const dataByMonth = {};
        stats.forEach(s => {
          dataByMonth[s.month] = s;
        });
        setMonthlyData(dataByMonth);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year]);

  // Check if a section has data
  const checkSectionComplete = (monthData, section) => {
    if (!monthData) return { complete: false, partial: false, fields: section.fields };
    
    let filledCount = 0;
    let hasAnyData = false;
    
    section.fields.forEach(field => {
      const value = monthData[field];
      if (value !== undefined && value !== null && value !== 0) {
        filledCount++;
        hasAnyData = true;
      } else if (value === 0) {
        // Zero is valid data for counts
        if (field.includes('count') || field.includes('sticks') || field.includes('sharps') || 
            field.includes('splashes') || field.includes('callins')) {
          filledCount++;
          hasAnyData = true;
        }
      }
    });
    
    // For census data, we need actual values (not zero)
    if (section.id === 'census' || section.id === 'device_days') {
      const hasValues = section.fields.some(f => monthData[f] > 0);
      return { 
        complete: hasValues, 
        partial: false, 
        fields: section.fields 
      };
    }
    
    // For compliance sections, need both fields filled
    if (section.id.includes('hygiene') || section.id.includes('ppe') || section.id.includes('eoc')) {
      const total = monthData[section.fields[1]];
      if (total && total > 0) {
        return { complete: true, partial: false, fields: section.fields };
      }
      return { complete: false, partial: false, fields: section.fields };
    }
    
    return { 
      complete: hasAnyData, 
      partial: filledCount > 0 && filledCount < section.fields.length,
      fields: section.fields 
    };
  };

  // Calculate month completion percentage
  const getMonthCompletion = (monthData) => {
    if (!monthData) return { percent: 0, requiredComplete: 0, requiredTotal: 0 };
    
    let requiredComplete = 0;
    let requiredTotal = 0;
    
    DATA_SECTIONS.forEach(section => {
      if (section.required) {
        requiredTotal++;
        const status = checkSectionComplete(monthData, section);
        if (status.complete) requiredComplete++;
      }
    });
    
    return {
      percent: requiredTotal > 0 ? Math.round((requiredComplete / requiredTotal) * 100) : 0,
      requiredComplete,
      requiredTotal
    };
  };

  // Get status badge for a month
  const getMonthStatus = (month) => {
    const data = monthlyData[month];
    const completion = getMonthCompletion(data);
    const isPastMonth = month < currentMonth || (month === currentMonth && new Date().getDate() > 15);
    
    if (completion.percent === 100) {
      return { status: "complete", label: "Complete", color: "bg-emerald-100 text-emerald-700" };
    } else if (completion.percent > 0) {
      return { status: "partial", label: "In Progress", color: "bg-amber-100 text-amber-700" };
    } else if (isPastMonth) {
      return { status: "overdue", label: "Data Due", color: "bg-red-100 text-red-700" };
    } else {
      return { status: "pending", label: "Upcoming", color: "bg-slate-100 text-slate-600" };
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-6xl mx-auto">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(12)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  // Find months that need attention
  const monthsNeedingData = MONTHS.filter(m => {
    const status = getMonthStatus(m);
    return status.status === "overdue" || (status.status === "partial" && m <= currentMonth);
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Data Entry Status
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track what data has been entered and what's still due
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28 border-[#6A26B5]/30" data-testid="status-year-selector">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {YEARS.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Alert for months needing data */}
      {monthsNeedingData.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-800">Data Entry Needed</p>
                <p className="text-sm text-red-700 mt-1">
                  The following months need data entry: {monthsNeedingData.map(m => getMonthName(m)).join(", ")}
                </p>
              </div>
              <Button 
                size="sm" 
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => navigate(`/monthly-entry?month=${monthsNeedingData[0]}&year=${year}`)}
                data-testid="enter-data-btn"
              >
                Enter Data
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Status Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {MONTHS.map(month => {
          const data = monthlyData[month];
          const completion = getMonthCompletion(data);
          const status = getMonthStatus(month);
          const isCurrentMonth = month === currentMonth;
          
          return (
            <Card 
              key={month} 
              className={`cursor-pointer hover:shadow-md transition-all ${
                isCurrentMonth ? "ring-2 ring-[#6A26B5]" : ""
              } ${status.status === "overdue" ? "border-red-300" : ""}`}
              onClick={() => navigate(`/monthly-entry?month=${month}&year=${year}`)}
              data-testid={`month-card-${month}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#6A26B5]" />
                    <span className="font-semibold text-slate-900">{getMonthName(month)}</span>
                  </div>
                  {isCurrentMonth && (
                    <Badge className="bg-[#6A26B5] text-white text-xs">Current</Badge>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Required sections</span>
                    <span className="font-mono font-medium">{completion.requiredComplete}/{completion.requiredTotal}</span>
                  </div>
                  <Progress 
                    value={completion.percent} 
                    className="h-2"
                  />
                </div>
                
                <div className="mt-3 flex items-center justify-between">
                  <Badge className={status.color}>
                    {status.status === "complete" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {status.status === "overdue" && <AlertCircle className="w-3 h-3 mr-1" />}
                    {status.label}
                  </Badge>
                  <Button variant="ghost" size="sm" className="text-[#6A26B5] p-0 h-auto">
                    <FileEdit className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Section Status for Current/Selected Month */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3 bg-[#f5f0fa] border-b">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ClipboardList className="w-5 h-5 text-[#6A26B5]" />
            Data Checklist - {getMonthName(currentMonth)} {year}
          </CardTitle>
          <p className="text-sm text-slate-500">
            Sections that need to be completed for the current month
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {DATA_SECTIONS.map(section => {
              const data = monthlyData[currentMonth];
              const status = checkSectionComplete(data, section);
              const Icon = section.icon;
              
              return (
                <div 
                  key={section.id} 
                  className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${
                    !status.complete && section.required ? "bg-amber-50/50" : ""
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    status.complete ? "bg-emerald-100" : section.required ? "bg-amber-100" : "bg-slate-100"
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      status.complete ? "text-emerald-600" : section.required ? "text-amber-600" : "text-slate-400"
                    }`} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{section.name}</p>
                      {section.required && (
                        <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">Required</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {status.complete ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    ) : status.partial ? (
                      <Circle className="w-6 h-6 text-amber-500" fill="#fef3c7" />
                    ) : (
                      <Circle className="w-6 h-6 text-slate-300" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Entry Button */}
      <div className="flex justify-center">
        <Button 
          size="lg"
          className="bg-[#6A26B5] hover:bg-[#5a1f9e] text-white px-8"
          onClick={() => navigate(`/monthly-entry?month=${currentMonth}&year=${year}`)}
          data-testid="enter-current-month-btn"
        >
          <FileEdit className="w-5 h-5 mr-2" />
          Enter {getMonthName(currentMonth)} Data
        </Button>
      </div>
    </div>
  );
}
